import React, { useState } from 'react'
import { PendingAction, PricingPlan } from './types'
import { PricingPlanFormValues, toNewPaymentPlans } from './pricingPlanFormSchema'
import { PricingPlansService } from './pricingPlansService'
import { ConfirmActionModal } from './modals/confirmActionModal'
import { ConfigurePricingPlanModal } from './modals/configurePricingPlanModal'
import { PricingPlanList } from './pricingPlanList'
import { PageLoader } from 'uiComponents/loaders'
import { MigratePricingPlanModal } from './modals/migratePricingPlanModal'
import { RemovePricingPlanModal } from './modals/removePricingPlanModal'
import { showErrorMessage } from 'uiComponents/message'
import './styles.scss'
import { useTranslation } from 'translations'

interface PricingPlansContainerProps {
    pricingPlans: PricingPlan[]
    pricingPlansService: PricingPlansService
    accountSlug: string
    articleUuid: string
    onUpdate: () => Promise<void>
    readOnly?: boolean
}

export const PricingPlansContainer = ({
    pricingPlans,
    pricingPlansService,
    accountSlug,
    articleUuid,
    onUpdate,
    readOnly,
}: PricingPlansContainerProps) => {
    const { t } = useTranslation()
    const [loading, setLoading] = useState(false)
    const [pendingAction, setPendingAction] = useState<PendingAction | null>(null)

    const activePricingPlan = pricingPlans.find((plan) => plan.status === 'active') ?? null
    const draftPricingPlan = pricingPlans.find((plan) => plan.status === 'draft') ?? null

    const dismissAction = () => setPendingAction(null)

    const handleAction = async (action: () => Promise<void>) => {
        setLoading(true)
        try {
            await action()
            await onUpdate()
        } catch (error) {
            const message = error instanceof Error ? error.message : t('products.pricing.pricingPlans.unexpectedError')
            showErrorMessage(message)
        }
        dismissAction()
        setLoading(false)
    }

    const handlePendingAction = (pending: PendingAction) => {
        switch (pending.action) {
            case 'stop_migrating':
            case 'stop_discontinuing':
                handleAction(() => pricingPlansService.abortAction(accountSlug, pending.pricingPlan!))
                break
            default:
                setPendingAction(pending)
        }
    }

    const activatePricingPlan = async (pricingPlan: PricingPlan) => {
        handleAction(() => pricingPlansService.activatePricingPlan(accountSlug, pricingPlan))
    }

    const discontinuePricingPlan = async (pricingPlan: PricingPlan) => {
        handleAction(() => pricingPlansService.discontinuePricingPlan(accountSlug, pricingPlan))
    }

    const savePricingPlan = async (formValues: PricingPlanFormValues) => {
        handleAction(async () => {
            const paymentPlans = toNewPaymentPlans(formValues, articleUuid)
            await pricingPlansService.createPricingPlan(accountSlug, paymentPlans)
        })
    }

    const migratePricingPlan = async (pricingPlan: PricingPlan) => {
        handleAction(() => pricingPlansService.migratePricingPlan(accountSlug, pricingPlan))
    }

    const removePricingPlan = async (pricingPlan: PricingPlan) => {
        handleAction(() => pricingPlansService.closePricingPlan(accountSlug, pricingPlan))
    }

    if (loading) {
        return (
            <div className="pricing-plans-container">
                <h5>{t('products.pricing.pricingPlans.title')}</h5>
                <PageLoader />
            </div>
        )
    }

    return (
        <div className="pricing-plans-container">
            {!readOnly && (
                <div className="pricing-plans-header">
                    <h5>{t('products.pricing.pricingPlans.title')}</h5>
                    <div
                        className="draft-pricing-plan"
                        onClick={() => handlePendingAction({ action: 'edit', pricingPlan: draftPricingPlan })}
                    >
                        <span>
                            {draftPricingPlan
                                ? t('products.pricing.pricingPlans.editDraft')
                                : t('products.pricing.pricingPlans.add')}
                        </span>
                    </div>
                </div>
            )}
            <PricingPlanList pricingPlans={pricingPlans} setPendingAction={handlePendingAction} readOnly={readOnly} />
            {pendingAction?.action === 'edit' && (
                <ConfigurePricingPlanModal
                    activePricingPlan={activePricingPlan}
                    draftPricingPlan={pendingAction.pricingPlan}
                    onSavePricingPlan={savePricingPlan}
                    onDismiss={dismissAction}
                />
            )}
            {pendingAction?.pricingPlan && (
                <>
                    {(pendingAction.action === 'activate' || pendingAction.action === 'discontinue') && (
                        <ConfirmActionModal
                            pricingPlan={pendingAction.pricingPlan}
                            action={pendingAction.action}
                            onConfirm={() =>
                                pendingAction.action === 'activate'
                                    ? activatePricingPlan(pendingAction.pricingPlan!)
                                    : discontinuePricingPlan(pendingAction.pricingPlan!)
                            }
                            onDismiss={dismissAction}
                        />
                    )}
                    {pendingAction.action === 'migrate' && activePricingPlan && (
                        <MigratePricingPlanModal
                            activePricingPlan={activePricingPlan}
                            migratingPricingPlan={pendingAction.pricingPlan}
                            onMigrate={() => migratePricingPlan(pendingAction.pricingPlan!)}
                            onDismiss={dismissAction}
                        />
                    )}
                    {pendingAction.action === 'remove' && (
                        <RemovePricingPlanModal
                            pricingPlan={pendingAction.pricingPlan}
                            onRemove={() => removePricingPlan(pendingAction.pricingPlan!)}
                            onDismiss={dismissAction}
                        />
                    )}
                </>
            )}
        </div>
    )
}
